/**
 * Copyright 2026 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import {
  authErrorHandler,
  createAction,
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  startLoading,
  stopLoading,
  snackbarErrorHandler,
  snackbarSuccessHandler
} from "openstack-uicore-foundation/lib/utils/actions";

import T from "i18n-react/dist/i18n-react";
import moment from "moment-timezone";
import {
  escapeFilterValue,
  getAccessTokenSafely,
  normalizeSelectAllField
} from "../utils/methods";
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_ORDER_DIR,
  DEFAULT_PER_PAGE
} from "../utils/constants";

export const REQUEST_SPONSOR_FORMS = "REQUEST_SPONSOR_FORMS";
export const RECEIVE_SPONSOR_FORMS = "RECEIVE_SPONSOR_FORMS";
export const RECEIVE_SPONSOR_FORM = "RECEIVE_SPONSOR_FORM";
export const SPONSOR_FORM_ARCHIVED = "SPONSOR_FORM_ARCHIVED";
export const SPONSOR_FORM_UNARCHIVED = "SPONSOR_FORM_UNARCHIVED";
export const SPONSOR_FORM_DELETED = "SPONSOR_FORM_DELETED";
export const REQUEST_GLOBAL_TEMPLATES = "REQUEST_GLOBAL_TEMPLATES";
export const RECEIVE_GLOBAL_TEMPLATES = "RECEIVE_GLOBAL_TEMPLATES";
export const GLOBAL_TEMPLATE_CLONED = "GLOBAL_TEMPLATE_CLONED";
export const TEMPLATE_FORM_CREATED = "TEMPLATE_FORM_CREATED";
export const RESET_TEMPLATE_FORM = "RESET_TEMPLATE_FORM";

export const REQUEST_SPONSOR_MANAGED_FORMS = "REQUEST_SPONSOR_MANAGED_FORMS";
export const RECEIVE_SPONSOR_MANAGED_FORMS = "RECEIVE_SPONSOR_MANAGED_FORMS";
export const REQUEST_SPONSOR_MANAGED_FORM = "REQUEST_SPONSOR_MANAGED_FORM";
export const RECEIVE_SPONSOR_MANAGED_FORM = "RECEIVE_SPONSOR_MANAGED_FORM";
export const SPONSOR_MANAGED_FORMS_ADDED = "SPONSOR_MANAGED_FORMS_ADDED";
export const SPONSOR_MANAGED_FORMS_UPGRADED = "SPONSOR_MANAGED_FORMS_UPGRADED";
export const SPONSOR_MANAGED_FORMS_OVERRIDEN =
  "SPONSOR_MANAGED_FORMS_OVERRIDEN";

export const REQUEST_SPONSOR_CUSTOMIZED_FORMS =
  "REQUEST_SPONSOR_CUSTOMIZED_FORMS";
export const RECEIVE_SPONSOR_CUSTOMIZED_FORMS =
  "RECEIVE_SPONSOR_CUSTOMIZED_FORMS";
export const RESET_SPONSOR_CUSTOMIZED_FORM = "RESET_SPONSOR_CUSTOMIZED_FORM";
export const SPONSOR_CUSTOMIZED_FORM_ADDED = "SPONSOR_CUSTOMIZED_FORM_ADDED";
export const SPONSOR_CUSTOMIZED_FORM_UPDATED =
  "SPONSOR_CUSTOMIZED_FORM_UPDATED";
export const RECEIVE_SPONSOR_CUSTOMIZED_FORM =
  "RECEIVE_SPONSOR_CUSTOMIZED_FORM";
export const SPONSOR_CUSTOMIZED_FORM_DELETED =
  "SPONSOR_CUSTOMIZED_FORM_DELETED";
export const SPONSOR_CUSTOMIZED_FORM_ARCHIVED_CHANGED =
  "SPONSOR_CUSTOMIZED_FORM_ARCHIVED_CHANGED";

export const RECEIVE_SPONSOR_CUSTOMIZED_FORM_ITEMS =
  "RECEIVE_SPONSOR_CUSTOMIZED_FORM_ITEMS";
export const REQUEST_SPONSOR_CUSTOMIZED_FORM_ITEMS =
  "REQUEST_SPONSOR_CUSTOMIZED_FORM_ITEMS";
export const RECEIVE_SPONSOR_CUSTOMIZED_FORM_ITEM =
  "RECEIVE_SPONSOR_CUSTOMIZED_FORM_ITEM";
export const SPONSOR_CUSTOMIZED_FORM_ITEM_DELETED =
  "SPONSOR_CUSTOMIZED_FORM_ITEM_DELETED";
export const SPONSOR_CUSTOMIZED_FORM_ITEM_ARCHIVED =
  "SPONSOR_CUSTOMIZED_FORM_ITEM_ARCHIVED";
export const SPONSOR_CUSTOMIZED_FORM_ITEM_UNARCHIVED =
  "SPONSOR_CUSTOMIZED_FORM_ITEM_UNARCHIVED";
export const UPDATE_SPONSOR_FORM_MANAGED_ITEM =
  "UPDATE_SPONSOR_FORM_MANAGED_ITEM";
export const SPONSOR_FORM_MANAGED_ITEM_UPDATED =
  "SPONSOR_FORM_MANAGED_ITEM_UPDATED";
export const SPONSOR_FORM_MANAGED_ITEM_ADDED =
  "SPONSOR_FORM_MANAGED_ITEM_ADDED";
export const SPONSOR_FORM_MANAGED_ITEM_DELETED =
  "SPONSOR_FORM_MANAGED_ITEM_DELETED";
export const SPONSOR_CUSTOMIZED_FORM_ITEMS_ADDED =
  "SPONSOR_CUSTOMIZED_FORM_ITEMS_ADDED";
export const RESET_SPONSOR_FORM_MANAGED_ITEM =
  "RESET_SPONSOR_FORM_MANAGED_ITEM";

// ITEMS
export const REQUEST_SPONSOR_FORM_ITEMS = "REQUEST_SPONSOR_FORM_ITEMS";
export const RECEIVE_SPONSOR_FORM_ITEMS = "RECEIVE_SPONSOR_FORM_ITEMS";
export const RECEIVE_SPONSOR_FORM_ITEM = "RECEIVE_SPONSOR_FORM_ITEM";
export const SPONSOR_FORM_ITEM_UPDATED = "SPONSOR_FORM_ITEM_UPDATED";
export const RESET_SPONSOR_FORM_ITEM = "RESET_SPONSOR_FORM_ITEM";
export const SPONSOR_FORM_ITEM_DELETED = "SPONSOR_FORM_ITEM_DELETED";
export const SPONSOR_FORM_ITEM_IMAGES_UPDATED =
  "SPONSOR_FORM_ITEM_IMAGES_UPDATED";
export const SPONSOR_FORM_ITEMS_ADDED = "SPONSOR_FORM_ITEMS_ADDED";
export const SPONSOR_FORM_ITEM_ARCHIVED = "SPONSOR_FORM_ITEM_ARCHIVED";
export const SPONSOR_FORM_ITEM_UNARCHIVED = "SPONSOR_FORM_ITEM_UNARCHIVED";

export const getSponsorForms =
  (
    term = "",
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR,
    showArchived = false,
    sponsorshipTypesId = []
  ) =>
  async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const { currentSummit } = currentSummitState;
    const accessToken = await getAccessTokenSafely();
    const filter = [];

    dispatch(startLoading());

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm},code=@${escapedTerm}`);
    }

    const params = {
      page,
      fields:
        "id,code,name,level,expire_date,is_archived,sponsorship_types,apply_to_all_types,opens_at,expires_at",
      relations: "items,sponsorship_types",
      per_page: perPage,
      access_token: accessToken,
      expand: "sponsorship_types"
    };

    if (!showArchived) {
      filter.push("is_archived==0");
    }

    if (sponsorshipTypesId?.length > 0) {
      const formattedSponsorships = sponsorshipTypesId.join("&&");
      filter.push("applies_to_all_tiers==0");
      filter.push(`sponsorship_type_id_not_in==${formattedSponsorships}`);
    }

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_SPONSOR_FORMS),
      createAction(RECEIVE_SPONSOR_FORMS),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms`,
      authErrorHandler,
      { order, orderDir, page, perPage, term, showArchived }
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const getSponsorForm = (formId) => async (dispatch, getState) => {
  const { currentSummitState } = getState();
  const { currentSummit } = currentSummitState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand: "meta_fields,meta_fields.values,sponsorship_types"
  };

  return getRequest(
    null,
    createAction(RECEIVE_SPONSOR_FORM),
    `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}`,
    authErrorHandler
  )(params)(dispatch).then(() => {
    dispatch(stopLoading());
  });
};

export const resetFormTemplate = () => (dispatch) => {
  dispatch(createAction(RESET_TEMPLATE_FORM)({}));
};

export const archiveSponsorForm = (formId) => async (dispatch, getState) => {
  const { currentSummitState } = getState();
  const accessToken = await getAccessTokenSafely();
  const { currentSummit } = currentSummitState;
  const params = { access_token: accessToken };

  dispatch(startLoading());

  return putRequest(
    null,
    createAction(SPONSOR_FORM_ARCHIVED),
    `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/archive`,
    null,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(() => {
      const { term, currentPage, perPage, order, orderDir, showArchived } =
        getState().sponsorFormsListState;
      return dispatch(
        getSponsorForms(
          term,
          currentPage,
          perPage,
          order,
          orderDir,
          showArchived
        )
      );
    })
    .catch(() => {})
    .finally(() => dispatch(stopLoading()));
};

export const unarchiveSponsorForm = (formId) => async (dispatch, getState) => {
  const { currentSummitState } = getState();
  const accessToken = await getAccessTokenSafely();
  const { currentSummit } = currentSummitState;
  const params = { access_token: accessToken };

  dispatch(startLoading());

  return deleteRequest(
    null,
    createAction(SPONSOR_FORM_UNARCHIVED)({ formId }),
    `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/archive`,
    null,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(() => {
      const { term, currentPage, perPage, order, orderDir, showArchived } =
        getState().sponsorFormsListState;
      return dispatch(
        getSponsorForms(
          term,
          currentPage,
          perPage,
          order,
          orderDir,
          showArchived
        )
      );
    })
    .catch(() => {})
    .finally(() => dispatch(stopLoading()));
};

export const deleteSponsorForm = (formId) => async (dispatch, getState) => {
  const { currentSummitState } = getState();
  const accessToken = await getAccessTokenSafely();
  const { currentSummit } = currentSummitState;
  const params = { access_token: accessToken };

  dispatch(startLoading());

  return deleteRequest(
    null,
    createAction(SPONSOR_FORM_DELETED)({ formId }),
    `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}`,
    null,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(() => {
      dispatch(
        snackbarSuccessHandler({
          title: T.translate("general.success"),
          html: T.translate("sponsor_forms.form_delete_success")
        })
      );
    })
    .finally(() => {
      dispatch(stopLoading());
    });
};

export const getGlobalTemplates =
  (
    term = null,
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR,
    showArchived = false
  ) =>
  async (dispatch) => {
    const accessToken = await getAccessTokenSafely();
    const filter = [];

    dispatch(startLoading());

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm},code=@${escapedTerm}`);
    }

    const params = {
      page,
      fields: "id,code,name,level,expire_date,is_archived",
      relations: "items",
      per_page: perPage,
      access_token: accessToken
    };

    filter.push(`is_archived==${showArchived ? 1 : 0}`);

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_GLOBAL_TEMPLATES),
      createAction(RECEIVE_GLOBAL_TEMPLATES),
      `${window.INVENTORY_API_BASE_URL}/api/v1/form-templates`,
      authErrorHandler,
      { order, orderDir, page, term }
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const cloneGlobalTemplate =
  (templateIds, sponsorIds, allSponsors) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    const normalizedEntity = {
      form_template_ids: templateIds,
      ...normalizeSelectAllField(
        sponsorIds,
        "apply_to_all_types",
        "sponsorship_types",
        allSponsors
      )
    };

    return postRequest(
      null,
      createAction(GLOBAL_TEMPLATE_CLONED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/clone`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(getSponsorForms());
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("sponsor_forms.global_template_popup.success")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const saveFormTemplate = (entity) => async (dispatch, getState) => {
  const { currentSummitState } = getState();
  const accessToken = await getAccessTokenSafely();
  const { currentSummit } = currentSummitState;

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  const normalizedEntity = normalizeFormTemplate(
    entity,
    currentSummit.time_zone_id
  );

  return postRequest(
    null,
    createAction(TEMPLATE_FORM_CREATED),
    `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms`,
    normalizedEntity,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(() => {
      dispatch(getSponsorForms());
      dispatch(
        snackbarSuccessHandler({
          title: T.translate("general.success"),
          html: T.translate("sponsor_forms.form_template_popup.created")
        })
      );
    })
    .finally(() => {
      dispatch(stopLoading());
    });
};

export const updateFormTemplate = (entity) => async (dispatch, getState) => {
  const { currentSummitState } = getState();
  const accessToken = await getAccessTokenSafely();
  const { currentSummit } = currentSummitState;

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  const normalizedEntity = normalizeFormTemplate(
    entity,
    currentSummit.time_zone_id
  );

  return putRequest(
    null,
    createAction(TEMPLATE_FORM_CREATED),
    `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${entity.id}`,
    normalizedEntity,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(() => {
      dispatch(getSponsorForms());
      dispatch(
        snackbarSuccessHandler({
          title: T.translate("general.success"),
          html: T.translate("sponsor_forms.form_template_popup.updated", {
            formName: entity.name
          })
        })
      );
    })
    .finally(() => {
      dispatch(stopLoading());
    });
};

export const updateFormTemplateTiers =
  ({ id, sponsorship_types = [], apply_to_all_types = false }) =>
  async (dispatch, getState) => {
    const { currentSummitState, sponsorFormsListState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const { term, currentPage, perPage, order, orderDir, showArchived } =
      sponsorFormsListState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    const normalizedEntity = normalizeSelectAllField(
      sponsorship_types,
      "apply_to_all_types",
      "sponsorship_types",
      apply_to_all_types
    );

    return putRequest(
      null,
      createAction(TEMPLATE_FORM_CREATED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${id}`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          getSponsorForms(
            term,
            currentPage,
            perPage,
            order,
            orderDir,
            showArchived
          )
        );
      })
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const normalizeFormTemplate = (entity, summitTZ) => {
  const normalizedEntity = { ...entity };
  const { opens_at, expires_at, sponsorship_types, meta_fields } = entity;

  normalizedEntity.opens_at = moment.tz(opens_at, summitTZ).unix();
  normalizedEntity.expires_at = moment.tz(expires_at, summitTZ).unix();

  Object.assign(
    normalizedEntity,
    normalizeSelectAllField(
      sponsorship_types,
      "apply_to_all_types",
      "sponsorship_types"
    )
  );

  normalizedEntity.meta_fields = meta_fields.filter((mf) => !!mf.name);

  return normalizedEntity;
};

/* ************************************************************************ */
/*         MANAGED FORMS       */
/* ************************************************************************ */

export const getSponsorManagedForms =
  (
    term = "",
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR,
    showArchived = false
  ) =>
  async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const summitTZ = currentSummit.time_zone.name;
    const filter = [];

    dispatch(startLoading());

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm},code=@${escapedTerm}`);
    }

    const params = {
      page,
      fields:
        "id,code,name,is_archived,opens_at,expires_at,items_count,allowed_add_ons",
      expands: "allowed_add_ons",
      per_page: perPage,
      access_token: accessToken
    };

    filter.push(`is_archived==${showArchived ? 1 : 0}`);

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_SPONSOR_MANAGED_FORMS),
      createAction(RECEIVE_SPONSOR_MANAGED_FORMS),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-forms`,
      authErrorHandler,
      { order, orderDir, page, perPage, term, summitTZ, showArchived }
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const saveSponsorManagedForm =
  (entity) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const normalizedEntity = normalizeSponsorManagedForm(entity);

    const params = {
      access_token: accessToken,
      fields:
        "id,code,name,is_archived,opens_at,expires_at,items_count,allowed_add_ons",
      relations: "allowed_add_ons"
    };

    return postRequest(
      null,
      createAction(SPONSOR_MANAGED_FORMS_ADDED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-forms`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const overrideSponsorManagedForm =
  (formId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return postRequest(
      null,
      createAction(SPONSOR_MANAGED_FORMS_OVERRIDEN),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-forms/${formId}/override`,
      {},
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(getSponsorManagedForms());
        dispatch(getSponsorCustomizedForms());
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const upgradeSponsorManagedForm =
  (entity) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const normalizedEntity = normalizeSponsorCustomizedForm(
      entity,
      currentSummit.time_zone.id
    );

    const params = {
      access_token: accessToken,
      fields:
        "id,code,name,is_archived,opens_at,expires_at,items_count,allowed_add_ons",
      relations: "allowed_add_ons"
    };

    return putRequest(
      null,
      createAction(SPONSOR_MANAGED_FORMS_UPGRADED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-forms/${entity.id}`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

const normalizeSponsorManagedForm = (entity) => {
  const normalizedEntity = {
    show_form_ids: entity.forms,
    ...normalizeSelectAllField(
      entity.add_ons,
      "apply_to_all_add_ons",
      "allowed_add_ons"
    )
  };

  return normalizedEntity;
};

export const getSponsorManagedForm = (formId) => async (dispatch, getState) => {
  const { currentSummitState, currentSponsorState } = getState();
  const { currentSummit } = currentSummitState;
  const {
    entity: { id: sponsorId }
  } = currentSponsorState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    fields:
      "id,code,name,is_archived,opens_at,expires_at,items_count,allowed_add_ons,instructions",
    expand:
      "allowed_add_ons,meta_fields,meta_fields.values,items,items.meta_fields,items.meta_fields.values,items.images",
    access_token: accessToken
  };

  return getRequest(
    null,
    createAction(RECEIVE_SPONSOR_CUSTOMIZED_FORM),
    `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-forms/${formId}`,
    snackbarErrorHandler
  )(params)(dispatch).then(() => {
    dispatch(stopLoading());
  });
};

/* ************************************************************************ */
/*         CUSTOMIZED FORMS       */
/* ************************************************************************ */

export const getSponsorCustomizedForms =
  (
    term = "",
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR,
    showArchived = false
  ) =>
  async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const summitTZ = currentSummit.time_zone.name;
    const filter = [];

    dispatch(startLoading());

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm},code=@${escapedTerm}`);
    }

    const params = {
      page,
      fields:
        "id,code,name,is_archived,opens_at,expires_at,items_count,allowed_add_ons,original_show_form_id",
      expands: "allowed_add_ons",
      per_page: perPage,
      access_token: accessToken
    };

    filter.push(`is_archived==${showArchived ? 1 : 0}`);

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_SPONSOR_CUSTOMIZED_FORMS),
      createAction(RECEIVE_SPONSOR_CUSTOMIZED_FORMS),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms`,
      authErrorHandler,
      { order, orderDir, page, perPage, term, summitTZ, showArchived }
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const getSponsorCustomizedForm =
  (formId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      fields:
        "id,code,name,is_archived,opens_at,instructions,expires_at,items_count,allowed_add_ons,meta_fields,apply_to_all_add_ons",
      expand: "allowed_add_ons,meta_fields,meta_fields.values",
      access_token: accessToken
    };

    return getRequest(
      null,
      createAction(RECEIVE_SPONSOR_CUSTOMIZED_FORM),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}`,
      authErrorHandler
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const getSponsorCustomizedFormItems =
  (
    formId,
    term = "",
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR,
    showArchived = false
  ) =>
  async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const filter = [];

    dispatch(startLoading());

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm},code=@${escapedTerm}`);
    }

    const params = {
      page,
      per_page: perPage,
      access_token: accessToken
    };

    filter.push(`is_archived==${showArchived ? 1 : 0}`);

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_SPONSOR_CUSTOMIZED_FORM_ITEMS),
      createAction(RECEIVE_SPONSOR_CUSTOMIZED_FORM_ITEMS),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/items`,
      authErrorHandler,
      { term, order, orderDir, page, perPage, showArchived }
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const saveSponsorCustomizedForm =
  (entity) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const normalizedEntity = normalizeSponsorCustomizedForm(
      entity,
      currentSummit.time_zone.id
    );

    const params = {
      access_token: accessToken,
      fields:
        "id,code,name,is_archived,opens_at,expires_at,items_count,allowed_add_ons",
      relations: "allowed_add_ons"
    };

    return postRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_FORM_ADDED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("edit_sponsor.forms_tab.customized_form.created")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const updateSponsorCustomizedForm =
  (entity) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const normalizedEntity = normalizeSponsorCustomizedForm(
      entity,
      currentSummit.time_zone.id
    );

    const params = {
      access_token: accessToken,
      fields:
        "id,code,name,is_archived,opens_at,expires_at,items_count,allowed_add_ons",
      relations: "allowed_add_ons"
    };

    return putRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_FORM_UPDATED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${entity.id}`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate(
              "edit_sponsor.forms_tab.customized_form.updated",
              { formName: entity.name }
            )
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const resetSponsorCustomizedForm = () => (dispatch) => {
  dispatch(createAction(RESET_SPONSOR_CUSTOMIZED_FORM)({}));
};

export const normalizeSponsorCustomizedForm = (entity, summitTZ) => {
  const {
    id,
    opens_at,
    expires_at,
    allowed_add_ons,
    meta_fields,
    ...normalizedEntity
  } = entity;

  if (opens_at)
    normalizedEntity.opens_at = moment.tz(opens_at, summitTZ).unix();
  if (expires_at)
    normalizedEntity.expires_at = moment.tz(expires_at, summitTZ).unix();

  Object.assign(
    normalizedEntity,
    normalizeSelectAllField(
      allowed_add_ons,
      "apply_to_all_add_ons",
      "allowed_add_ons"
    )
  );

  normalizedEntity.meta_fields = meta_fields.filter((mf) => !!mf.name);

  delete normalizedEntity.sponsorship_types;

  return normalizedEntity;
};

export const archiveSponsorCustomizedForm =
  (formId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return putRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_FORM_ARCHIVED_CHANGED)({
        formId,
        isArchived: true
      }),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/archive`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("edit_sponsor.forms_tab.customized_form.archived")
          })
        );
        const { term, showArchived, customizedForms } =
          getState().sponsorPageFormsListState;
        const { currentPage, perPage, order, orderDir } = customizedForms;
        return dispatch(
          getSponsorCustomizedForms(
            term,
            currentPage,
            perPage,
            order,
            orderDir,
            showArchived
          )
        );
      })
      .catch(() => {})
      .finally(() => dispatch(stopLoading()));
  };

export const unarchiveSponsorCustomizedForm =
  (formId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return deleteRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_FORM_ARCHIVED_CHANGED)({
        formId,
        isArchived: false
      }),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/archive`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate(
              "edit_sponsor.forms_tab.customized_form.unarchived"
            )
          })
        );
        const { term, showArchived, customizedForms } =
          getState().sponsorPageFormsListState;
        const { currentPage, perPage, order, orderDir } = customizedForms;
        return dispatch(
          getSponsorCustomizedForms(
            term,
            currentPage,
            perPage,
            order,
            orderDir,
            showArchived
          )
        );
      })
      .catch(() => {})
      .finally(() => dispatch(stopLoading()));
  };

export const deleteSponsorCustomizedForm =
  (formId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return deleteRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_FORM_DELETED)({ formId }),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("sponsor_forms.form_delete_success")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

/* ************************************************************************ */
/*         ITEMS       */
/* ************************************************************************ */

export const getSponsorFormItems =
  (
    formId,
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR,
    showArchived = false
  ) =>
  async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const { currentSummit } = currentSummitState;
    const accessToken = await getAccessTokenSafely();
    const filter = [];

    dispatch(startLoading());

    const params = {
      page,
      fields:
        "id,code,name,early_bird_rate,standard_rate,onsite_rate,default_quantity,images,images.file_url,images.created,is_archived",
      expand: "images",
      per_page: perPage,
      access_token: accessToken
    };

    filter.push(`is_archived==${showArchived ? 1 : 0}`);

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_SPONSOR_FORM_ITEMS),
      createAction(RECEIVE_SPONSOR_FORM_ITEMS),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items`,
      authErrorHandler,
      { order, orderDir, page, perPage, showArchived }
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const getSponsorFormItem =
  (formId, itemId) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const { currentSummit } = currentSummitState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken,
      expand: "meta_fields,meta_fields.values,images"
    };

    return getRequest(
      null,
      createAction(RECEIVE_SPONSOR_FORM_ITEM),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items/${itemId}`,
      authErrorHandler
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const deleteSponsorFormItem =
  (formId, itemId) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const params = { access_token: accessToken };

    dispatch(startLoading());
    return deleteRequest(
      null,
      createAction(SPONSOR_FORM_ITEM_DELETED)({ itemId }),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items/${itemId}`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("sponsor_form_item_list.item_delete_success")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

const saveItemImages =
  (formId, formItemId, images) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const { currentSummit } = currentSummitState;
    const accessToken = await getAccessTokenSafely();
    const params = { access_token: accessToken };

    const promises = images.map((file) => {
      if (file.id) {
        return putRequest(
          null,
          createAction(SPONSOR_FORM_ITEM_IMAGES_UPDATED),
          `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items/${formItemId}/images/${file.id}`,
          file,
          authErrorHandler,
          file
        )(params)(dispatch);
      }
      return postRequest(
        null,
        createAction(SPONSOR_FORM_ITEM_IMAGES_UPDATED),
        `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items/${formItemId}/images`,
        file,
        authErrorHandler,
        file
      )(params)(dispatch);
    });

    return Promise.all(promises);
  };

export const saveSponsorFormItem =
  (formId, entity) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    const normalizedEntity = normalizeItem(entity);

    return postRequest(
      null,
      createAction(SPONSOR_FORM_ITEM_UPDATED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(({ response }) => {
        const promises = [Promise.resolve(0)];

        if (normalizedEntity.images?.length > 0) {
          const savingImages = saveItemImages(
            formId,
            response.id,
            normalizedEntity.images
          )(dispatch, getState);

          promises.push(savingImages);
        }

        return Promise.all(promises).then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: T.translate("general.success"),
              html: T.translate("sponsor_form_item_list.edit_item.created")
            })
          );
        });
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const updateSponsorFormItem =
  (formId, entity) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    const normalizedEntity = normalizeItem(entity);

    return putRequest(
      null,
      createAction(SPONSOR_FORM_ITEM_UPDATED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items/${entity.id}`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        const promises = [Promise.resolve(0)];

        if (normalizedEntity.images?.length > 0) {
          const savingImages = saveItemImages(
            formId,
            entity.id,
            normalizedEntity.images
          )(dispatch, getState);

          promises.push(savingImages);
        }

        return Promise.all(promises).then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: T.translate("general.success"),
              html: T.translate("sponsor_form_item_list.edit_item.updated")
            })
          );
        });
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const archiveSponsorFormItem =
  (formId, itemId) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return putRequest(
      null,
      createAction(SPONSOR_FORM_ITEM_ARCHIVED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items/${itemId}/archive`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        const { currentPage, perPage, order, orderDir, showArchived } =
          getState().sponsorFormItemsListState;
        return dispatch(
          getSponsorFormItems(
            formId,
            currentPage,
            perPage,
            order,
            orderDir,
            showArchived
          )
        );
      })
      .catch(() => {})
      .finally(() => dispatch(stopLoading()));
  };

export const unarchiveSponsorFormItem =
  (formId, itemId) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return deleteRequest(
      null,
      createAction(SPONSOR_FORM_ITEM_UNARCHIVED)({ itemId }),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items/${itemId}/archive`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        const { currentPage, perPage, order, orderDir, showArchived } =
          getState().sponsorFormItemsListState;
        return dispatch(
          getSponsorFormItems(
            formId,
            currentPage,
            perPage,
            order,
            orderDir,
            showArchived
          )
        );
      })
      .catch(() => {})
      .finally(() => dispatch(stopLoading()));
  };

export const resetSponsorFormItem = () => (dispatch) => {
  dispatch(createAction(RESET_SPONSOR_FORM_ITEM)({}));
};

const normalizeItem = (entity) => {
  const normalizedEntity = { ...entity };
  const {
    meta_fields,
    quantity_limit_per_show,
    quantity_limit_per_sponsor,
    default_quantity,
    images
  } = entity;

  if (meta_fields) {
    normalizedEntity.meta_fields = meta_fields.filter((mf) => !!mf.name);
  }

  if (images) {
    normalizedEntity.images = images?.filter((img) => img.file_path);
  }

  if (quantity_limit_per_show === "")
    delete normalizedEntity.quantity_limit_per_show;
  if (quantity_limit_per_sponsor === "")
    delete normalizedEntity.quantity_limit_per_sponsor;
  if (default_quantity === "") delete normalizedEntity.default_quantity;

  return normalizedEntity;
};

export const addInventoryItems =
  (formId, itemIds) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return postRequest(
      null,
      createAction(SPONSOR_FORM_ITEMS_ADDED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/show-forms/${formId}/items/clone`,
      { inventory_item_ids: itemIds },
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate(
              "sponsor_form_item_list.add_from_inventory.items_added"
            )
          })
        );
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const saveSponsorFormManagedItem =
  (formId, entity) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    const normalizedEntity = normalizeManagedItem(entity);

    if (entity.id) {
      return putRequest(
        createAction(UPDATE_SPONSOR_FORM_MANAGED_ITEM),
        createAction(SPONSOR_FORM_MANAGED_ITEM_UPDATED),
        `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/items/${entity.id}`,
        normalizedEntity,
        snackbarErrorHandler,
        entity
      )(params)(dispatch)
        .then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: T.translate("general.success"),
              html: T.translate(
                "edit_sponsor.forms_tab.form_manage_items.item_updated"
              )
            })
          );
        })
        .finally(() => {
          dispatch(stopLoading());
        });
    }

    const successMessage = {
      title: T.translate("general.done"),
      html: T.translate(
        "edit_sponsor.forms_tab.form_manage_items.item_created"
      ),
      type: "success"
    };

    return postRequest(
      createAction(UPDATE_SPONSOR_FORM_MANAGED_ITEM),
      createAction(SPONSOR_FORM_MANAGED_ITEM_ADDED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/items`,
      normalizedEntity,
      snackbarErrorHandler,
      entity
    )(params)(dispatch)
      .then(() => {
        dispatch(snackbarSuccessHandler(successMessage));
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const resetSponsorFormManagedItem = () => (dispatch) => {
  dispatch(createAction(RESET_SPONSOR_FORM_MANAGED_ITEM)({}));
};

const normalizeManagedItem = (entity) => {
  const normalizedEntity = { ...entity };
  normalizedEntity.meta_fields = normalizedEntity.meta_fields?.filter(
    (mf) => mf.name
  );
  normalizedEntity.images = normalizedEntity.images?.filter(
    (img) => img.file_path
  );

  return normalizedEntity;
};

export const deleteSponsorFormManagedItem =
  (formId, itemId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return deleteRequest(
      null,
      createAction(SPONSOR_FORM_MANAGED_ITEM_DELETED)({ itemId }),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/items/${itemId}`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("sponsor_forms.form_delete_success")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const getSponsorFormManagedItem =
  (formId, itemId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken,
      expands: "meta_fields,meta_fields.values,images"
    };

    return getRequest(
      null,
      createAction(RECEIVE_SPONSOR_CUSTOMIZED_FORM_ITEM),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/items/${itemId}`,
      authErrorHandler
    )(params)(dispatch).then(() => {
      dispatch(stopLoading());
    });
  };

export const addSponsorManagedFormItems =
  (formId, itemIds) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return postRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_FORM_ITEMS_ADDED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/items/clone`,
      { inventory_item_ids: itemIds },
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(getSponsorCustomizedFormItems(formId));
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate(
              "sponsor_form_item_list.add_from_inventory.items_added"
            )
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const archiveSponsorCustomizedFormItem =
  (formId, itemId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return putRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_FORM_ITEM_ARCHIVED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/items/${itemId}/archive`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        const { term, currentPage, perPage, order, orderDir, showArchived } =
          getState().sponsorCustomizedFormItemsListState;
        return dispatch(
          getSponsorCustomizedFormItems(
            formId,
            term,
            currentPage,
            perPage,
            order,
            orderDir,
            showArchived
          )
        );
      })
      .catch(() => {})
      .finally(() => dispatch(stopLoading()));
  };

export const unarchiveSponsorCustomizedFormItem =
  (formId, itemId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return deleteRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_FORM_ITEM_UNARCHIVED)({ itemId }),
      `${window.PURCHASES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-forms/${formId}/items/${itemId}/archive`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        const { term, currentPage, perPage, order, orderDir, showArchived } =
          getState().sponsorCustomizedFormItemsListState;
        return dispatch(
          getSponsorCustomizedFormItems(
            formId,
            term,
            currentPage,
            perPage,
            order,
            orderDir,
            showArchived
          )
        );
      })
      .catch(() => {})
      .finally(() => dispatch(stopLoading()));
  };
